import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Sparkles, TrendingUp, TrendingDown, Wallet, Loader2, AlertTriangle, CheckCircle2, Lightbulb, ShoppingBasket, FileDown, ChefHat } from "lucide-react";
import { generateAnalyticsPdf } from "../lib/analyticsPdf";

const Delta = ({ v, invert, testid }) => {
  if (v == null) return <span className="text-xs text-muted-foreground" data-testid={testid}>— vs prec.</span>;
  const good = invert ? v <= 0 : v >= 0;
  return <span className={`text-xs font-medium ${good ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid={testid}>{v > 0 ? "+" : ""}{v}% vs prec.</span>;
};

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PIE_COLORS = ["#C24127", "#E08A5B", "#7A8450", "#C9A227", "#4F6D7A", "#9B5DE5", "#B5651D", "#5C946E", "#A44A3F", "#D9A566", "#6B7A8F", "#8A9B68"];
const iso = (d) => d.toISOString().slice(0, 10);

function presetRange(key) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (key === "this_month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (key === "last_month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (key === "30d") { const s = new Date(now); s.setDate(s.getDate() - 29); return { from: iso(s), to: iso(now) }; }
  if (key === "year") return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) };
  return { from: "", to: "" };
}

const HEALTH = {
  buono: { label: "Andamento buono", cls: "text-[hsl(var(--success))]", Icon: CheckCircle2, ring: "border-[hsl(var(--success))]/40" },
  attenzione: { label: "Da tenere d'occhio", cls: "text-amber-500", Icon: AlertTriangle, ring: "border-amber-500/40" },
  critico: { label: "Situazione critica", cls: "text-destructive", Icon: AlertTriangle, ring: "border-destructive/40" },
};

export default function Analytics() {
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(presetRange("this_month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [foodCost, setFoodCost] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: range.from, to_date: range.to };
      const [ov, fc] = await Promise.all([api.get("/analytics/overview", { params }), api.get("/sales/real-food-cost", { params })]);
      setData(ov.data); setFoodCost(fc.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [range.from, range.to]);

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const business = await api.get("/settings/business").then((r) => r.data).catch(() => ({}));
      await generateAnalyticsPdf({ data, ai, foodCost, range, business });
      toast.success("Report PDF scaricato");
    } catch (e) { toast.error("Errore nella generazione del PDF"); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => { setPreset(key); setAi(null); if (key !== "custom") setRange(presetRange(key)); };

  const runAi = async () => {
    setAiBusy(true); setAi(null);
    try {
      const { data } = await api.post("/ai/accounting-analysis", null, { params: { from_date: range.from, to_date: range.to } });
      setAi(data.analysis);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const inc = data?.income?.total || 0;
  const exp = data?.expense?.total || 0;
  const profit = data?.profit || 0;
  const health = ai ? (HEALTH[ai.health] || HEALTH.attenzione) : null;

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Analisi" subtitle="Statistiche e analisi AI su acquisti e vendite" testid="analytics-header" />

      {/* Periodo */}
      <Card className="p-4 rounded-xl mb-6" data-testid="period-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {[["this_month", "Questo mese"], ["last_month", "Mese scorso"], ["30d", "Ultimi 30 gg"], ["year", "Quest'anno"], ["custom", "Personalizzato"]].map(([k, l]) => (
              <Button key={k} size="sm" variant={preset === k ? "default" : "outline"} className="rounded-full" onClick={() => applyPreset(k)} data-testid={`preset-${k}`}>{l}</Button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={range.from} onChange={(e) => { setRange({ ...range, from: e.target.value }); setAi(null); }} className="h-9" data-testid="from-date" /></div>
              <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={range.to} onChange={(e) => { setRange({ ...range, to: e.target.value }); setAi(null); }} className="h-9" data-testid="to-date" /></div>
            </div>
          )}
          <Button variant="outline" className="rounded-full ml-auto" onClick={exportPdf} disabled={pdfBusy || !data} data-testid="export-pdf-button">
            {pdfBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Report PDF
          </Button>
          <Button className="rounded-full" onClick={runAi} disabled={aiBusy || loading} data-testid="run-ai-button">
            {aiBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {aiBusy ? "Analizzo…" : "Analisi AI"}
          </Button>
        </div>
      </Card>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Carico le statistiche…</div>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 rounded-xl" data-testid="kpi-income">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase"><TrendingUp className="w-4 h-4 text-[hsl(var(--success))]" /> Incassi</div>
              <p className="font-dash text-2xl font-semibold mt-1">{eur(inc)}</p>
              <p className="text-xs text-muted-foreground mt-1">Uff. {eur(data?.income?.ufficiale)} · Prev. {eur(data?.income?.black)}</p>
              <Delta v={data?.delta?.income} testid="delta-income" />
            </Card>
            <Card className="p-4 rounded-xl" data-testid="kpi-expense">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase"><TrendingDown className="w-4 h-4 text-destructive" /> Spese</div>
              <p className="font-dash text-2xl font-semibold mt-1">{eur(exp)}</p>
              <p className="text-xs text-muted-foreground mt-1">Uff. {eur(data?.expense?.ufficiale)} · Fuori cassa {eur(data?.expense?.fuori_cassa)}</p>
              <Delta v={data?.delta?.expense} invert testid="delta-expense" />
            </Card>
            <Card className="p-4 rounded-xl" data-testid="kpi-profit">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase"><Wallet className="w-4 h-4 text-primary" /> Utile</div>
              <p className={`font-dash text-2xl font-semibold mt-1 ${profit >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{eur(profit)}</p>
              <p className="text-xs text-muted-foreground mt-1">Margine {data?.margin_pct ?? 0}%</p>
              <Delta v={data?.delta?.profit} testid="delta-profit" />
            </Card>
            <Card className="p-4 rounded-xl" data-testid="kpi-avg">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase"><TrendingUp className="w-4 h-4 text-primary" /> Incasso medio/gg</div>
              <p className="font-dash text-2xl font-semibold mt-1">{eur(data?.avg_daily_income)}</p>
              <p className="text-xs text-muted-foreground mt-1">{data?.income_days || 0} giorni con incassi</p>
              <Delta v={data?.delta?.avg_daily_income} testid="delta-avg" />
            </Card>
          </div>
          {data?.previous && (
            <p className="text-xs text-muted-foreground -mt-3 mb-6" data-testid="previous-period">Confronto con il periodo precedente {data.previous.from_date} → {data.previous.to_date} (incassi {eur(data.previous.income?.total)} · spese {eur(data.previous.expense?.total)} · utile {eur(data.previous.profit)})</p>
          )}

          {/* Food cost reale */}
          <Card className="p-5 rounded-xl mb-6" data-testid="real-food-cost-card">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <ChefHat className="w-5 h-5 text-primary" /><h3 className="font-dash font-semibold text-lg">Food cost reale</h3>
              <span className="text-xs text-muted-foreground">venduto × ricette Food Cost</span>
              {foodCost?.rows?.length > 0 && (
                <span className={`ml-auto font-dash text-2xl font-semibold ${foodCost.fc_pct < 30 ? "text-[hsl(var(--success))]" : foodCost.fc_pct <= 40 ? "text-amber-500" : "text-destructive"}`} data-testid="real-fc-pct">{foodCost.fc_pct}%</span>
              )}
            </div>
            {foodCost?.rows?.length ? (
              <>
                <p className="text-sm text-muted-foreground mb-3">Ricavi piatti abbinati {eur(foodCost.revenue)} · Costo materie prime {eur(foodCost.cost)}{foodCost.unmatched_revenue ? ` · Venduto senza ricetta ${eur(foodCost.unmatched_revenue)}` : ""}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs uppercase text-muted-foreground text-left"><th className="py-1">Ricetta</th><th className="text-right">Venduti</th><th className="text-right">Ricavo</th><th className="text-right">Costo</th><th className="text-right">Margine</th><th className="text-right">FC %</th></tr></thead>
                    <tbody>
                      {foodCost.rows.slice(0, 10).map((r) => (
                        <tr key={r.recipe_id} className="border-t border-border/60" data-testid={`fc-row-${r.recipe_id}`}>
                          <td className="py-1.5 font-medium">{r.recipe}</td><td className="text-right">{r.qty}</td><td className="text-right">{eur(r.revenue)}</td><td className="text-right">{eur(r.cost)}</td><td className="text-right">{eur(r.margin)}</td>
                          <td className={`text-right font-semibold ${r.fc_pct < 30 ? "text-[hsl(var(--success))]" : r.fc_pct <= 40 ? "text-amber-500" : "text-destructive"}`}>{r.fc_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <p className="text-sm text-muted-foreground">Nessun piatto venduto abbinato a una ricetta nel periodo. Importa il PDF "Venduto Prodotti" da Import Cassa e abbina i piatti alle ricette in <b>Vendite</b>.</p>}
          </Card>

          {/* AI analysis */}
          {ai && (
            <Card className={`p-5 rounded-xl mb-6 border ${health.ring}`} data-testid="ai-analysis">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-dash font-semibold text-lg">Analisi AI</h3>
                <span className={`ml-auto flex items-center gap-1.5 text-sm font-medium ${health.cls}`}><health.Icon className="w-4 h-4" /> {health.label}</span>
              </div>
              <p className="text-sm mb-4" data-testid="ai-summary">{ai.summary}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--success))] mb-2"><CheckCircle2 className="w-4 h-4" /> Punti di forza</div>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">{(ai.strengths || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-[hsl(var(--success))]">•</span>{s}</li>)}</ul>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-amber-500 mb-2"><AlertTriangle className="w-4 h-4" /> Attenzione</div>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">{(ai.warnings || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">•</span>{s}</li>)}</ul>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-primary mb-2"><Lightbulb className="w-4 h-4" /> Consigli</div>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">{(ai.suggestions || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-primary">•</span>{s}</li>)}</ul>
                </div>
              </div>
            </Card>
          )}

          {/* Trend */}
          <Card className="p-5 rounded-xl mb-6" data-testid="trend-card">
            <h3 className="font-dash font-semibold mb-4">Andamento incassi vs spese</h3>
            <div className="h-72">
              {data?.trend?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7A8450" stopOpacity={0.5} /><stop offset="95%" stopColor="#7A8450" stopOpacity={0} /></linearGradient>
                      <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#C24127" stopOpacity={0.5} /><stop offset="95%" stopColor="#C24127" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend />
                    <Area type="monotone" dataKey="incassi" name="Incassi" stroke="#7A8450" fill="url(#gInc)" strokeWidth={2} />
                    <Area type="monotone" dataKey="spese" name="Spese" stroke="#C24127" fill="url(#gExp)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Nessun movimento nel periodo.</div>}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Spese per categoria */}
            <Card className="p-5 rounded-xl" data-testid="expense-cat-card">
              <h3 className="font-dash font-semibold mb-4">Spese per categoria</h3>
              <div className="h-72">
                {data?.expense_by_category?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.expense_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={95} label={(e) => e.category}>
                        {data.expense_by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => eur(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Nessuna spesa nel periodo.</div>}
              </div>
            </Card>

            {/* Top prodotti */}
            <Card className="p-5 rounded-xl" data-testid="top-products-card">
              <div className="flex items-center gap-2 mb-4"><ShoppingBasket className="w-4 h-4 text-primary" /><h3 className="font-dash font-semibold">Prodotti più venduti</h3></div>
              <div className="h-72">
                {data?.top_products?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_products.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="product" width={110} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => eur(v)} />
                      <Bar dataKey="amount" name="Venduto" fill="#C24127" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-1">
                    <ShoppingBasket className="w-8 h-8 opacity-40" />
                    <p>Nessun venduto per prodotto nel periodo.</p>
                    <p className="text-xs">Carica il venduto giornaliero da <b>Import Cassa</b> (PDF).</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
